import React, { useEffect, useState, useCallback, useMemo } from "react";
//-tech-debt-react-query;
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, CircularProgress, Grid, Card, CardHeader,
  List, ListItemButton, ListItemText, ListItemIcon, Checkbox, Box, Typography,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, InputAdornment, TablePagination
} from "@mui/material";
import { Search } from "@mui/icons-material";

// Replace with your actual query utilities
import { fetchItems, fetchItem, postItem, putItem } from "../../support/queries";
import { HbrDivider } from "@harbor/elements-react-standalone";
import { CacheKey } from "../../types/cache-keys";

type Role = { id: string | number; name: string };
type AzureADUser = {
  id: string; // Azure AD GUID
  mail?: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
};
type DBUser = {
  id: string | number;
  azure_ad_guid?: string;
  email?: string;
};

export type AzureADGroup = {
  id?: string | number;
  group_id: string;
  name: string;
  description?: string;
  roles?: (string | number)[];
  users?: (string | number)[];
};

interface Props {
  open: boolean;
  editItem: AzureADGroup | null;
  onClose: () => void;
}

function not(a: readonly string[], b: readonly string[]): string[] {
  return a.filter((value) => !b.includes(value));
}
function intersection(a: readonly string[], b: readonly string[]): string[] {
  return a.filter((value) => b.includes(value));
}
function union(a: readonly string[], b: readonly string[]): string[] {
  return [...a, ...not(b, a)];
}

const TRANSFER_LIST_WIDTH = 320;

const userColumns = [
  { key: 'mail', label: 'Email' },
  { key: 'givenName', label: 'First Name' },
  { key: 'surname', label: 'Last Name' },
  { key: 'id', label: 'Azure AD GUID' },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const AzureADGroupEditDialog: React.FC<Props> = ({ open, editItem, onClose }) => {
  const queryClient = useQueryClient();

  // Core fields
  const group = editItem;
  const [groupId, setGroupId] = useState(group?.group_id ?? "");
  const [name, setName] = useState(group?.name ?? "");
  const [description, setDescription] = useState(group?.description ?? "");

  // Roles
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [assignedRoles, setAssignedRoles] = useState<string[]>([]);
  const [checkedRoles, setCheckedRoles] = useState<string[]>([]);

  // Azure AD Users
  const [azureGroupUsers, setAzureGroupUsers] = useState<AzureADUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Search/sort for user table
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: "mail",
    direction: "asc"
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch roles and group assignments, and Azure AD users only
  const fetchAll = useCallback(async () => {
    setGroupId(group?.group_id ?? "");
    setName(group?.name ?? "");
    setDescription(group?.description ?? "");
    setLoading(true);

    // Fetch roles and group details
    let roles: Role[] = [];
    let groupDetail: any = {};
    try {
      [roles, groupDetail] = await Promise.all([
        fetchItems("db/role"),
        group?.id ? fetchItem(`db/azure-ad-group/${group.id}`) : Promise.resolve({ roles: [] }),
      ]);
    } catch {
      setLoading(false);
      return;
    }
    setAllRoles(roles);
    setAssignedRoles(((groupDetail.roles || []) as (string | number)[]).map(String));
    setCheckedRoles([]);

    // Fetch Azure AD group members
    let azureUsers: AzureADUser[] = [];
    try {
      if (group?.group_id) {
        azureUsers = await fetchItems(`auth/azure/group/${group.group_id}/members/`,
          { size: 2000, refresh: true});
      }
    } catch {
      setLoading(false);
      return;
    }

    // Fetch DB users (for GUID/email matching only)
    let dbUsers: DBUser[] = [];
    try {
      dbUsers = await fetchItems("db/user");
    } catch {
      // If fails, just skip update logic
    }

    // For each Azure user: try to match by GUID, then email; if match by email but no GUID, update DB user
    const dbGuidMap: Record<string, DBUser> = {};
    const dbEmailMap: Record<string, DBUser> = {};
    dbUsers.forEach((u) => {
      if (u.azure_ad_guid) dbGuidMap[u.azure_ad_guid] = u;
      if (u.email) dbEmailMap[u.email.toLowerCase()] = u;
    });

    for (const azUser of azureUsers) {
      if (azUser.id && dbGuidMap[azUser.id]) continue; // Already matched by guid
      // Not matched by guid, try email
      const mail = azUser.mail?.toLowerCase();
      if (mail && dbEmailMap[mail] && !dbEmailMap[mail].azure_ad_guid) {
        // Update DB user to add guid
        await putItem("db/user", {
          ...dbEmailMap[mail],
          azure_ad_guid: azUser.id,
        });
        dbGuidMap[azUser.id] = { ...dbEmailMap[mail], azure_ad_guid: azUser.id }; // update map too
      }
    }

    setAzureGroupUsers(azureUsers);
    setLoading(false);
    setPage(0); // Reset pagination on refresh
  }, [group]);

  useEffect(() => {
    if (open) fetchAll();
    // eslint-disable-next-line
  }, [group, open]);

  // Role transfer logic -- unchanged from your original
  const allRoleIds = allRoles.map(r => String(r.id));
  const leftRoles = not(allRoleIds, assignedRoles);
  const rightRoles = assignedRoles;
  const leftCheckedRoles = intersection(checkedRoles, leftRoles);
  const rightCheckedRoles = intersection(checkedRoles, rightRoles);

  const handleToggleRole = (value: string) => () => {
    setCheckedRoles(prev => {
      const currentIndex = prev.indexOf(value);
      const newChecked = [...prev];
      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);
      return newChecked;
    });
  };
  const numberOfCheckedRoles = (items: readonly string[]) =>
    intersection(checkedRoles, items).length;
  const handleToggleAllRoles = (items: readonly string[]) => () => {
    if (numberOfCheckedRoles(items) === items.length) {
      setCheckedRoles(not(checkedRoles, items));
    } else {
      setCheckedRoles(union(checkedRoles, items));
    }
  };
  const handleCheckedRightRoles = () => {
    setAssignedRoles(union(assignedRoles, leftCheckedRoles));
    setCheckedRoles(not(checkedRoles, leftCheckedRoles));
  };
  const handleCheckedLeftRoles = () => {
    setAssignedRoles(not(assignedRoles, rightCheckedRoles));
    setCheckedRoles(not(checkedRoles, rightCheckedRoles));
  };

  const customRoleList = (title: React.ReactNode, itemIds: readonly string[]) => (
    <Card sx={{ minWidth: TRANSFER_LIST_WIDTH, width: TRANSFER_LIST_WIDTH }}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAllRoles(itemIds)}
            checked={numberOfCheckedRoles(itemIds) === itemIds.length && itemIds.length !== 0}
            indeterminate={
              numberOfCheckedRoles(itemIds) !== itemIds.length && numberOfCheckedRoles(itemIds) !== 0
            }
            disabled={itemIds.length === 0}
            inputProps={{
              'aria-label': 'all items selected',
            }}
          />
        }
        title={title}
        subheader={`${numberOfCheckedRoles(itemIds)}/${itemIds.length} selected`}
      />
      <HbrDivider />
      <List
        sx={{
          width: TRANSFER_LIST_WIDTH,
          height: 220,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        dense
        component="div"
        role="list"
      >
        {itemIds.map((id) => {
          const role = allRoles.find(r => String(r.id) === id);
          if (!role) return null;
          const labelId = `transfer-list-role-item-${id}-label`;

          return (
            <ListItemButton
              key={id}
              role="listitem"
              onClick={handleToggleRole(id)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checkedRoles.includes(id)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={role.name}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      let groupIdValue = group?.id;
      let responseGroup: any = null;

      if (!groupIdValue) {
        responseGroup = await postItem("db/azure-ad-group", {
          group_id: groupId,
          name,
          description,
          roles: assignedRoles,
        });
        groupIdValue = responseGroup.id;
      } else {
        responseGroup = await putItem("db/azure-ad-group", {
          id: groupIdValue,
          group_id: groupId,
          name,
          description,
          roles: assignedRoles,
        });
      }
      if (responseGroup) {
        queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, `azureAdGroups`] });
      }
      setSaving(false);
      onClose();
    } catch (err) {
      setSaving(false);
      alert('Failed to save Azure AD group or assignments.');
    }
  };

  // --- User Table: search and sort ---
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return azureGroupUsers;
    return azureGroupUsers.filter((user) =>
      userColumns.some((col) => {
        const value = user[col.key as keyof AzureADUser];
        return value && value.toString().toLowerCase().includes(searchTerm);
      })
    );
  }, [azureGroupUsers, searchTerm]);

  const sortedUsers = useMemo(() => {
    if (!sortConfig) return filteredUsers;
    const { key, direction } = sortConfig;
    return [...filteredUsers].sort((a, b) => {
      const aValue = a[key as keyof AzureADUser] || "";
      const bValue = b[key as keyof AzureADUser] || "";
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  // Paginate users
  const pagedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedUsers.slice(start, start + rowsPerPage);
  }, [sortedUsers, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{group?.id ? 'Edit Azure AD Group' : 'Add Azure AD Group'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Group ID"
          value={groupId}
          onChange={e => setGroupId(e.target.value)}
          fullWidth
          margin="normal"
          disabled
        />
        <TextField
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          margin="normal"
          disabled
        />
        <TextField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          fullWidth
          margin="normal"
          disabled
        />
        {/* Roles Transfer */}
        <Typography sx={{ mt: 2, mb: 1, fontWeight: 500 }}>Assign Roles</Typography>
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: 'center', alignItems: 'center' }}
        >
          <Grid item>{customRoleList('Available Roles', leftRoles)}</Grid>
          <Grid item>
            <Grid container direction="column" sx={{ alignItems: 'center' }}>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedRightRoles}
                disabled={leftCheckedRoles.length === 0}
                aria-label="move selected right"
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedLeftRoles}
                disabled={rightCheckedRoles.length === 0}
                aria-label="move selected left"
              >
                &lt;
              </Button>
            </Grid>
          </Grid>
          <Grid item>{customRoleList('Assigned Roles', rightRoles)}</Grid>
        </Grid>
        {/* Users Table */}
        <Typography sx={{ mt: 3, mb: 1, fontWeight: 500 }}>
          Azure AD Group Members
          <Button
            size="small"
            sx={{ ml: 2 }}
            onClick={fetchAll}
            disabled={loading}
          >
            Refresh Membership
          </Button>
        </Typography>
        <Box mb={2}>
          <TextField
            placeholder="Search members..."
            variant="outlined"
            size="small"
            fullWidth
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Paper sx={{ width: "100%", overflowX: "auto", mb: 2 }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {userColumns.map(col => (
                    <TableCell key={col.key}>
                      <TableSortLabel
                        active={sortConfig?.key === col.key}
                        direction={sortConfig?.key === col.key ? sortConfig.direction : "asc"}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedUsers.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={userColumns.length} align="center">
                      No group members found.
                    </TableCell>
                  </TableRow>
                )}
                {pagedUsers.map(user => (
                  <TableRow key={user.id}>
                    {userColumns.map(col => (
                      <TableCell key={col.key}>
                        {user[col.key as keyof AzureADUser] || ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={userColumns.length} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows per page"
          />
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={loading || saving} variant="contained" color="primary">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AzureADGroupEditDialog;